import React from "react";
import { Autocomplete, TextField, Box, Typography, CircularProgress } from "@mui/material";
import { autocompletePopperSlotProps } from "../../utils/autocompletePopperSlotProps";

const defaultSx = { "& .MuiOutlinedInput-root": { borderRadius: 2 } };

/**
 * Liste déroulante avec recherche (Autocomplete MUI).
 * La liste s'ouvre toujours sous le champ pour garder le champ de saisie visible.
 */
const SearchableSelect = ({
    options = [],
    value,
    onChange,
    multiple = false,
    label,
    placeholder,
    loading = false,
    disabled = false,
    required = false,
    helperText,
    getOptionValue = (opt) => opt?.value ?? opt?.id ?? opt?.key ?? opt,
    getOptionLabel = (opt) => {
        if (opt == null) return "";
        if (typeof opt === "string" || typeof opt === "number") return String(opt);
        return opt.label ?? opt.name ?? opt.title ?? String(getOptionValue(opt));
    },
    isOptionEqualToValue = (a, b) => String(getOptionValue(a)) === String(getOptionValue(b)),
    renderOption,
    noOptionsText = "Aucun résultat",
    filterSelectedOptions = true,
    sx = defaultSx,
    clearable = true,
}) => {
    const resolveValue = () => {
        if (multiple) {
            const ids = Array.isArray(value) ? value.map(String) : [];
            return options.filter((opt) => ids.includes(String(getOptionValue(opt))));
        }
        if (value == null || value === "") return null;
        return options.find((opt) => String(getOptionValue(opt)) === String(value)) || null;
    };

    const handleChange = (_, next) => {
        if (multiple) {
            onChange((next || []).map((opt) => getOptionValue(opt)));
            return;
        }
        onChange(next ? getOptionValue(next) : "");
    };

    const defaultRenderOption = (props, option) => (
        <Box component="li" {...props} key={String(getOptionValue(option))}>
            <Typography fontSize={13} fontWeight={600}>{getOptionLabel(option)}</Typography>
            {option?.description && (
                <Typography component="span" fontSize={11} color="text.secondary" ml={1}>
                    {option.description}
                </Typography>
            )}
        </Box>
    );

    return (
        <Autocomplete
            multiple={multiple}
            options={options}
            value={resolveValue()}
            onChange={handleChange}
            disabled={disabled}
            loading={loading}
            filterSelectedOptions={filterSelectedOptions}
            isOptionEqualToValue={isOptionEqualToValue}
            getOptionLabel={getOptionLabel}
            noOptionsText={loading ? "Chargement…" : noOptionsText}
            disableClearable={!clearable && !multiple}
            slotProps={autocompletePopperSlotProps}
            renderOption={renderOption || defaultRenderOption}
            renderInput={(params) => (
                <TextField
                    {...params}
                    required={required}
                    label={label}
                    placeholder={placeholder}
                    helperText={helperText}
                    size="small"
                    sx={sx}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {loading ? <CircularProgress size={16} /> : null}
                                {params.InputProps.endAdornment}
                            </>
                        ),
                    }}
                />
            )}
        />
    );
};

export default SearchableSelect;
